import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  AppBar,
  Toolbar,
  Button,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Paper,
  IconButton,
  Badge,
  Tabs,
  Tab,
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Logout,
  Notifications,
  Security,
  TrendingUp,
  People,
  CheckCircle,
  Cancel,
  Schedule,
  Warning,
  ManageAccounts,
  BusinessCenter,
  FiberManualRecord,
  Payment,
  LocalOffer,
  Pets,
  Pause,
  SupportAgent,
  Email,
} from '@mui/icons-material';
import { useAdminAuth } from '../context/AdminAuthContext';
import { AdminStats } from '../types/admin';
import ProfileApprovalList from './ProfileApprovalList';
import AdminSettings from './AdminSettings';
import UserManagement from './UserManagement';
import ServiceProviderList from './ServiceProviderList';
import MobileUsersList from './MobileUsersList';
import StripeDashboard from './StripeDashboard';
import DiscountCodeManager from './DiscountCodeManager';
import PetOwnerProfilesList from './PetOwnerProfilesList';
import SupportTicketList from './SupportTicketList';
import EmailTemplateManager from './EmailTemplateManager';

const AdminDashboard: React.FC = () => {
  const { admin, logout, logActivity } = useAdminAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showServiceProviders, setShowServiceProviders] = useState(false);
  const [showStripePayments, setShowStripePayments] = useState(false);
  const [showDiscountCodes, setShowDiscountCodes] = useState(false);
  const [showPetOwners, setShowPetOwners] = useState(false);
  const [showSupportTickets, setShowSupportTickets] = useState(false);
  const [showEmailTemplates, setShowEmailTemplates] = useState(false);
  const [statDialog, setStatDialog] = useState<'pending' | 'approved' | 'rate' | 'response' | null>(null);
  const [statDialogData, setStatDialogData] = useState<any[]>([]);
  const [statDialogLoading, setStatDialogLoading] = useState(false);
  const [profileTab, setProfileTab] = useState(0);
  const [openProfileId, setOpenProfileId] = useState<string | null>(null);
  const [stats, setStats] = useState<AdminStats>({
    totalPending: 0,
    totalApproved: 0,
    totalRejected: 0,
    totalSuspended: 0,
    averageResponseTime: 0,
    approvalRate: 0,
    urgentCount: 0,
  });

  // Notification dropdown state
  const [notifAnchorEl, setNotifAnchorEl] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);

  const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://pawtel-app.onrender.com';

  useEffect(() => {
    // Fetch dashboard stats and notifications
    fetchDashboardStats();
    fetchNotifications();

    // Log dashboard access
    logActivity('view_dashboard');

    // Set up real-time updates
    const interval = setInterval(() => {
      fetchDashboardStats();
      fetchNotifications();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const res = await fetch(`${API_BASE}/api/admin/profiles/stats`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStats({
          totalPending: data.totalPending,
          totalApproved: data.totalApproved,
          totalRejected: data.totalRejected,
          totalSuspended: data.totalSuspended,
          averageResponseTime: data.averageResponseTime,
          approvalRate: data.approvalRate,
          urgentCount: data.urgentCount,
        });
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const openStatDialog = async (type: 'pending' | 'approved' | 'rate' | 'response') => {
    setStatDialog(type);
    setStatDialogLoading(true);
    setStatDialogData([]);
    try {
      const token = localStorage.getItem('admin_token');
      let url = '';
      if (type === 'pending') {
        url = `${API_BASE}/api/admin/profiles/pending`;
      } else if (type === 'approved') {
        url = `${API_BASE}/api/admin/profiles?status=approved&limit=100`;
      } else {
        // rate + response: fetch all actioned profiles
        url = `${API_BASE}/api/admin/profiles?status=approved&limit=100`;
      }
      const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        let profiles = Array.isArray(data) ? data : (data.profiles || []);

        if (type === 'rate' || type === 'response') {
          // Also fetch rejected to show full picture
          const rejRes = await fetch(`${API_BASE}/api/admin/profiles?status=rejected&limit=100`, {
            headers: { 'Authorization': `Bearer ${token}` },
          });
          if (rejRes.ok) {
            const rejData = await rejRes.json();
            profiles = [...profiles, ...(rejData.profiles || [])];
          }
          // Calculate response time per profile
          profiles = profiles.map((p: any) => {
            const actionedAt = p.status === 'approved' ? p.approvedAt : p.rejectedAt;
            let responseHours = null;
            if (p.submittedAt && actionedAt) {
              responseHours = Math.round((new Date(actionedAt).getTime() - new Date(p.submittedAt).getTime()) / (1000 * 60 * 60));
            }
            return { ...p, responseHours };
          });
          profiles.sort((a: any, b: any) => new Date(b.submittedAt).getTime() - new Date(a.submittedAt).getTime());
        }

        setStatDialogData(profiles);
      }
    } catch (err) {
      console.error('Failed to load stat dialog data:', err);
    } finally {
      setStatDialogLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      setNotifLoading(true);
      const token = localStorage.getItem('admin_token');
      // Fetch pending profiles and service change notifications in parallel
      const [profileRes, serviceChangeRes, responseTimeRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/profiles/pending`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE}/api/admin/service-notifications?unreadOnly=true`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${API_BASE}/api/admin/response-time-alerts?unreadOnly=true`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      const allNotifs: any[] = [];

      if (profileRes.ok) {
        const profiles = await profileRes.json();
        const profileNotifs = (Array.isArray(profiles) ? profiles : []).map((p: any) => ({
          id: p._id || p.id,
          type: 'pending_profile',
          name: `${p.firstName} ${p.lastName}`,
          email: p.email,
          serviceType: p.serviceType || 'sitter',
          submittedAt: new Date(p.submittedAt),
          urgency: p.urgency || 'medium',
          isUrgent: p.submittedAt && (Date.now() - new Date(p.submittedAt).getTime()) > 24 * 60 * 60 * 1000,
          profileImage: p.sitterInfo?.profileImage || null,
        }));
        allNotifs.push(...profileNotifs);
      }

      if (serviceChangeRes.ok) {
        const serviceChanges = await serviceChangeRes.json();
        const changeNotifs = (Array.isArray(serviceChanges) ? serviceChanges : []).map((n: any) => ({
          id: n._id || n.id,
          type: 'service_change',
          changeType: n.changeType,
          name: n.providerName,
          email: n.providerEmail,
          serviceName: n.serviceName,
          changes: n.changes,
          submittedAt: new Date(n.createdAt),
          isUrgent: false,
        }));
        allNotifs.push(...changeNotifs);
      }

      if (responseTimeRes.ok) {
        const responseAlerts = await responseTimeRes.json();
        const alertNotifs = (Array.isArray(responseAlerts) ? responseAlerts : []).map((a: any) => ({
          id: a._id || a.id,
          type: 'response_time_alert',
          name: a.providerName,
          email: a.providerEmail,
          ownerName: a.ownerName,
          hoursElapsed: a.hoursElapsed,
          missedResponses: 0,
          submittedAt: new Date(a.createdAt),
          isUrgent: true,
        }));
        allNotifs.push(...alertNotifs);
      }

      // Sort all by date, most recent first
      allNotifs.sort((a, b) => b.submittedAt.getTime() - a.submittedAt.getTime());
      setNotifications(allNotifs);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setNotifLoading(false);
    }
  };

  const handleNotifOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleNotifClose = () => {
    setNotifAnchorEl(null);
  };

  const getTimeAgo = (date: Date): string => {
    const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
  };

  const serviceTypeLabels: Record<string, string> = {
    sitter: '🐕 Pet Sitter',
    groomer: '✂️ Groomer',
    vet: '🏥 Vet',
    trainer: '🎾 Trainer',
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleProfileMenuClose();
    await logout();
  };

  const handleOpenSettings = () => {
    handleProfileMenuClose();
    setShowSettings(true);
  };

  const handleOpenUserManagement = () => {
    handleProfileMenuClose();
    setShowUserManagement(true);
  };

  const handleOpenServiceProviders = () => {
    handleProfileMenuClose();
    setShowServiceProviders(true);
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: 'primary' | 'secondary' | 'success' | 'error' | 'warning';
    subtitle?: string;
    onClick?: () => void;
  }> = ({ title, value, icon, color, subtitle, onClick }) => (
    <Card elevation={2} sx={{ cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { boxShadow: 6 } : {} }} onClick={onClick}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography color="text.secondary" gutterBottom variant="body2">
              {title}
            </Typography>
            <Typography variant="h4" component="div" fontWeight="bold">
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: `${color}.main`,
              color: 'white',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Top App Bar */}
      <AppBar position="sticky" sx={{ backgroundColor: '#000' }}>
        <Toolbar>
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
            <img
              src="/pawtel-logo-white.png"
              alt="Pawtel"
              style={{ height: 32 }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Notifications */}
            <IconButton color="inherit" onClick={handleNotifOpen}>
              <Badge badgeContent={notifications.length} color="error">
                <Notifications />
              </Badge>
            </IconButton>

            {/* Admin Profile */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2">
                Welcome, {admin?.name || admin?.email}
              </Typography>
              <Chip
                label={admin?.role.replace('_', ' ').toUpperCase()}
                size="small"
                color="secondary"
              />
              <IconButton
                onClick={handleProfileMenuOpen}
                sx={{ p: 0 }}
              >
                <Avatar
                  sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}
                >
                  {admin?.name?.charAt(0) || admin?.email?.charAt(0) || 'A'}
                </Avatar>
              </IconButton>
            </Box>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleOpenSettings}>
          <Security sx={{ mr: 1 }} />
          Admin Settings
        </MenuItem>
        <MenuItem onClick={handleOpenUserManagement}>
          <ManageAccounts sx={{ mr: 1 }} />
          User Management
        </MenuItem>
        <MenuItem onClick={handleOpenServiceProviders}>
          <BusinessCenter sx={{ mr: 1 }} />
          Service Providers
        </MenuItem>
        <MenuItem onClick={() => { handleProfileMenuClose(); setShowPetOwners(true); }}>
          <Pets sx={{ mr: 1 }} />
          Pet Owners
        </MenuItem>
        <MenuItem onClick={() => { handleProfileMenuClose(); setShowStripePayments(true); }}>
          <Payment sx={{ mr: 1 }} />
          Stripe Payments
        </MenuItem>
        <MenuItem onClick={() => { handleProfileMenuClose(); setShowDiscountCodes(true); }}>
          <LocalOffer sx={{ mr: 1 }} />
          Discount Codes
        </MenuItem>
        <MenuItem onClick={() => { handleProfileMenuClose(); setShowSupportTickets(true); }}>
          <SupportAgent sx={{ mr: 1 }} />
          Support Tickets
        </MenuItem>
        <MenuItem onClick={() => { handleProfileMenuClose(); setShowEmailTemplates(true); }}>
          <Email sx={{ mr: 1 }} />
          Email Templates
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <Logout sx={{ mr: 1 }} />
          Logout
        </MenuItem>
      </Menu>

      {/* Notifications Dropdown */}
      <Popover
        open={Boolean(notifAnchorEl)}
        anchorEl={notifAnchorEl}
        onClose={handleNotifClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { width: 400, maxHeight: 480 } } }}
      >
        <Box sx={{ p: 2, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight="bold">
            Notifications
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {notifications.length} {notifications.length === 1 ? 'item' : 'items'} awaiting review
          </Typography>
        </Box>

        {notifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <CheckCircle sx={{ fontSize: 48, color: 'success.main', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              All caught up! No pending items.
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, overflow: 'auto', maxHeight: 360 }}>
            {notifications.map((notif, idx) => (
              <React.Fragment key={`${notif.type}-${notif.id}`}>
                {idx > 0 && <Divider />}
                {notif.type === 'response_time_alert' ? (
                  <ListItem
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      bgcolor: 'error.50',
                      py: 1.5,
                    }}
                    onClick={async () => {
                      try {
                        const token = localStorage.getItem('admin_token');
                        await fetch(`${API_BASE}/api/admin/response-time-alerts/${notif.id}/read`, {
                          method: 'PUT',
                          headers: { 'Authorization': `Bearer ${token}` },
                        });
                        fetchNotifications();
                      } catch (e) { /* ignore */ }
                      handleNotifClose();
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'error.main', width: 40, height: 40 }}>⏰</Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="subtitle2" noWrap>
                          {notif.name} — No Response
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" component="div" color="error.main" fontWeight="600">
                            Has not responded to {notif.ownerName} in {notif.hoursElapsed}+ hours
                          </Typography>
                          <Typography variant="caption" component="span" color="text.secondary">
                            {getTimeAgo(notif.submittedAt)}
                          </Typography>
                        </Box>
                      }
                    />
                    <FiberManualRecord sx={{ fontSize: 10, color: 'error.main' }} />
                  </ListItem>
                ) : notif.type === 'service_change' ? (
                  <ListItem
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      py: 1.5,
                    }}
                    onClick={async () => {
                      // Mark as read
                      try {
                        const token = localStorage.getItem('admin_token');
                        await fetch(`${API_BASE}/api/admin/service-notifications/${notif.id}/read`, {
                          method: 'PUT',
                          headers: { 'Authorization': `Bearer ${token}` },
                        });
                        fetchNotifications();
                      } catch (e) { /* ignore */ }
                      handleNotifClose();
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: notif.changeType === 'service_deleted' ? 'error.main' : 'warning.main',
                          width: 40,
                          height: 40,
                        }}
                      >
                        {notif.changeType === 'service_deleted' ? '✕' : '✎'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle2" noWrap>
                            {notif.name}
                          </Typography>
                          <Chip
                            label={notif.changeType === 'service_deleted' ? 'Deleted' : 'Modified'}
                            size="small"
                            color={notif.changeType === 'service_deleted' ? 'error' : 'warning'}
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" component="span">
                            {notif.changeType === 'service_deleted'
                              ? `Deleted service: ${notif.serviceName}`
                              : `Updated service: ${notif.serviceName}`}
                          </Typography>
                          {notif.changeType === 'service_updated' && notif.changes && (
                            <Typography variant="caption" component="div" color="text.secondary">
                              Changed: {Object.keys(notif.changes).join(', ')}
                            </Typography>
                          )}
                          <Typography variant="caption" component="span" color="text.secondary">
                            {getTimeAgo(notif.submittedAt)}
                          </Typography>
                        </Box>
                      }
                    />
                    <FiberManualRecord sx={{ fontSize: 10, color: notif.changeType === 'service_deleted' ? 'error.main' : 'warning.main' }} />
                  </ListItem>
                ) : (
                  <ListItem
                    sx={{
                      cursor: 'pointer',
                      '&:hover': { bgcolor: 'action.hover' },
                      bgcolor: notif.isUrgent ? 'error.50' : 'transparent',
                      py: 1.5,
                    }}
                    onClick={() => {
                      handleNotifClose();
                      setProfileTab(0);
                      setShowSettings(false);
                      setShowUserManagement(false);
                      setShowServiceProviders(false);
                      setOpenProfileId(notif.id);
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        src={notif.profileImage || undefined}
                        sx={{
                          bgcolor: notif.isUrgent ? 'error.main' : 'primary.main',
                          width: 40,
                          height: 40,
                        }}
                      >
                        {notif.name.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="subtitle2" noWrap>
                            {notif.name}
                          </Typography>
                          {notif.isUrgent && (
                            <Chip label="Urgent" size="small" color="error" sx={{ height: 20, fontSize: '0.65rem' }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" component="span">
                            {serviceTypeLabels[notif.serviceType] || notif.serviceType}
                          </Typography>
                          <Typography variant="caption" component="span" sx={{ mx: 0.5 }}>•</Typography>
                          <Typography variant="caption" component="span" color={notif.isUrgent ? 'error.main' : 'text.secondary'}>
                            {getTimeAgo(notif.submittedAt)}
                          </Typography>
                        </Box>
                      }
                    />
                    <FiberManualRecord sx={{ fontSize: 10, color: notif.isUrgent ? 'error.main' : 'primary.main' }} />
                  </ListItem>
                )}
              </React.Fragment>
            ))}
          </List>
        )}

        {notifications.length > 0 && (
          <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Button
              size="small"
              onClick={() => {
                handleNotifClose();
                setProfileTab(0);
                setShowSettings(false);
                setShowUserManagement(false);
                setShowServiceProviders(false);
                setTimeout(() => {
                  document.getElementById('profile-management-section')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
            >
              View All Pending Profiles
            </Button>
          </Box>
        )}
      </Popover>

      {/* Dashboard Content */}
      <Box sx={{ p: 3 }}>
        {/* Welcome Section */}
        <Box mb={3}>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Dashboard Overview
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Monitor and manage service provider approvals
          </Typography>
        </Box>

        {/* Stats Grid */}
        <Grid container spacing={3} mb={4}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Pending Approvals"
              value={stats.totalPending}
              icon={<Schedule />}
              color="warning"
              subtitle="Awaiting review"
              onClick={() => openStatDialog('pending')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Approved"
              value={stats.totalApproved}
              icon={<CheckCircle />}
              color="success"
              subtitle="Total approved"
              onClick={() => openStatDialog('approved')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Approval Rate"
              value={`${stats.approvalRate}%`}
              icon={<TrendingUp />}
              color="primary"
              subtitle="Last 30 days"
              onClick={() => openStatDialog('rate')}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Avg Response"
              value={`${stats.averageResponseTime}h`}
              icon={<Schedule />}
              color="secondary"
              subtitle="Response time"
              onClick={() => openStatDialog('response')}
            />
          </Grid>
        </Grid>

        {/* Stat Card Dialog */}
        <Dialog open={!!statDialog} onClose={() => setStatDialog(null)} maxWidth="md" fullWidth>
          <DialogTitle>
            {statDialog === 'pending' && 'Pending Approvals'}
            {statDialog === 'approved' && 'Approved Service Providers'}
            {statDialog === 'rate' && 'Approval Rate Breakdown'}
            {statDialog === 'response' && 'Response Time Details'}
          </DialogTitle>
          <DialogContent dividers>
            {statDialogLoading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : statDialogData.length === 0 ? (
              <Typography color="text.secondary" textAlign="center" py={4}>
                No profiles found.
              </Typography>
            ) : statDialog === 'pending' ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Service Type</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Waiting</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statDialogData.map((p: any) => {
                      const hours = Math.round((Date.now() - new Date(p.submittedAt).getTime()) / (1000 * 60 * 60));
                      return (
                        <TableRow key={p._id} sx={hours > 24 ? { bgcolor: 'error.50' } : {}}>
                          <TableCell>{p.firstName} {p.lastName}</TableCell>
                          <TableCell>{p.email}</TableCell>
                          <TableCell>
                            <Chip label={serviceTypeLabels[p.serviceType] || p.serviceType || 'Sitter'} size="small" />
                          </TableCell>
                          <TableCell>{new Date(p.submittedAt).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Chip
                              label={hours < 24 ? `${hours}h` : `${Math.floor(hours / 24)}d ${hours % 24}h`}
                              size="small"
                              color={hours > 24 ? 'error' : 'default'}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : statDialog === 'approved' ? (
              <>
                {Object.entries(
                  statDialogData.reduce((acc: Record<string, any[]>, p: any) => {
                    const type = serviceTypeLabels[p.serviceType] || p.serviceType || 'Pet Sitter';
                    if (!acc[type]) acc[type] = [];
                    acc[type].push(p);
                    return acc;
                  }, {})
                ).map(([type, profiles]) => (
                  <Box key={type} mb={3}>
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      {type} ({profiles.length})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Approved On</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {profiles.map((p: any) => (
                            <TableRow key={p._id}>
                              <TableCell>{p.firstName} {p.lastName}</TableCell>
                              <TableCell>{p.email}</TableCell>
                              <TableCell>{p.approvedAt ? new Date(p.approvedAt).toLocaleDateString() : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                ))}
              </>
            ) : (
              /* rate + response */
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Service Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Submitted</TableCell>
                      <TableCell>Actioned</TableCell>
                      <TableCell>Response Time</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statDialogData.map((p: any) => {
                      const actionedAt = p.status === 'approved' ? p.approvedAt : p.rejectedAt;
                      return (
                        <TableRow key={p._id}>
                          <TableCell>{p.firstName} {p.lastName}</TableCell>
                          <TableCell>
                            <Chip label={serviceTypeLabels[p.serviceType] || p.serviceType || 'Sitter'} size="small" />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={p.status}
                              size="small"
                              color={p.status === 'approved' ? 'success' : 'error'}
                            />
                          </TableCell>
                          <TableCell>{p.submittedAt ? new Date(p.submittedAt).toLocaleDateString() : '-'}</TableCell>
                          <TableCell>{actionedAt ? new Date(actionedAt).toLocaleDateString() : '-'}</TableCell>
                          <TableCell>
                            {p.responseHours !== null ? (
                              <Chip
                                label={p.responseHours < 24 ? `${p.responseHours}h` : `${Math.floor(p.responseHours / 24)}d ${p.responseHours % 24}h`}
                                size="small"
                                color={p.responseHours <= 24 ? 'success' : p.responseHours <= 48 ? 'warning' : 'error'}
                              />
                            ) : '-'}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </DialogContent>
        </Dialog>

        {/* Settings Modal */}
        {showSettings && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                maxWidth: '90vw',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
              }}
            >
              <AdminSettings onClose={() => setShowSettings(false)} />
            </Box>
          </Box>
        )}

        {/* User Management Modal */}
        {showUserManagement && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                maxWidth: '95vw',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
              }}
            >
              <UserManagement onClose={() => setShowUserManagement(false)} />
            </Box>
          </Box>
        )}

        {/* Service Provider Management Modal */}
        {showServiceProviders && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                maxWidth: '98vw',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
              }}
            >
              <ServiceProviderList onClose={() => setShowServiceProviders(false)} />
            </Box>
          </Box>
        )}

        {/* Stripe Payments Modal */}
        {showStripePayments && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                maxWidth: '95vw',
                width: '1200px',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
                p: 3,
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                <Button onClick={() => setShowStripePayments(false)} variant="outlined" size="small">
                  Close
                </Button>
              </Box>
              <StripeDashboard />
            </Box>
          </Box>
        )}

        {/* Discount Code Management Modal */}
        {showDiscountCodes && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                maxWidth: '95vw',
                width: '1200px',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
                p: 3,
              }}
            >
              <DiscountCodeManager onClose={() => setShowDiscountCodes(false)} />
            </Box>
          </Box>
        )}

        {/* Pet Owners Modal */}
        {showPetOwners && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                maxWidth: '95vw',
                width: '1200px',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
                p: 3,
              }}
            >
              <PetOwnerProfilesList onClose={() => setShowPetOwners(false)} />
            </Box>
          </Box>
        )}

        {/* Support Tickets Modal */}
        {showSupportTickets && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                maxWidth: '95vw',
                width: '1200px',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
                p: 3,
              }}
            >
              <SupportTicketList onClose={() => setShowSupportTickets(false)} />
            </Box>
          </Box>
        )}

        {/* Email Templates Modal */}
        {showEmailTemplates && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              zIndex: 1300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'auto',
            }}
          >
            <Box
              sx={{
                backgroundColor: 'white',
                borderRadius: 2,
                maxWidth: '95vw',
                width: '1200px',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative',
              }}
            >
              <EmailTemplateManager onClose={() => setShowEmailTemplates(false)} />
            </Box>
          </Box>
        )}

        {/* Urgent Alerts */}
        {stats.urgentCount > 0 && (
          <Paper
            sx={{
              p: 2,
              mb: 3,
              backgroundColor: 'error.light',
              color: 'error.contrastText',
              border: '1px solid',
              borderColor: 'error.main',
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Warning />
              <Typography variant="h6" fontWeight="bold">
                Urgent Action Required
              </Typography>
            </Box>
            <Typography variant="body2">
              {stats.urgentCount} profile{stats.urgentCount !== 1 ? 's' : ''} pending approval for more than 24 hours
            </Typography>
          </Paper>
        )}

        {/* Profile Management Section */}
        <Paper id="profile-management-section" sx={{ p: 3 }}>
          <Box mb={2} display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h5" fontWeight="bold">
              Service Provider Management
            </Typography>
            <Button
              variant="outlined"
              startIcon={<People />}
              onClick={() => window.location.reload()}
            >
              Refresh
            </Button>
          </Box>

          <Tabs
            value={profileTab}
            onChange={(_, v) => setProfileTab(v)}
            sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label={`Pending (${stats.totalPending})`} icon={<Schedule />} iconPosition="start" />
            <Tab label={`Approved (${stats.totalApproved})`} icon={<CheckCircle />} iconPosition="start" />
            <Tab label={`Rejected (${stats.totalRejected})`} icon={<Cancel />} iconPosition="start" />
            <Tab label={`Suspended (${stats.totalSuspended})`} icon={<Pause />} iconPosition="start" />
          </Tabs>

          {profileTab === 0 && (
            <ProfileApprovalList
              statusFilter="pending"
              openProfileId={openProfileId}
              onProfileOpened={() => setOpenProfileId(null)}
            />
          )}
          {profileTab === 1 && <ProfileApprovalList statusFilter="approved" />}
          {profileTab === 2 && <ProfileApprovalList statusFilter="rejected" />}
          {profileTab === 3 && <ProfileApprovalList statusFilter="suspended" />}
        </Paper>

        {/* Mobile Users Section */}
        <Paper sx={{ p: 3, mt: 3 }}>
          <Box mb={2}>
            <Typography variant="h5" fontWeight="bold">
              Mobile App Users
            </Typography>
            <Typography variant="body2" color="text.secondary">
              All registered users with platform details
            </Typography>
          </Box>
          <MobileUsersList />
        </Paper>
      </Box>
    </Box>
  );
};

export default AdminDashboard;